import JSZip from 'jszip';
import DOMPurify from 'dompurify';
import { toastManager } from '@/ui/components/toast';
import {
  Document,
  type FilesPageRow,
  GetProxyIndexedDocumentsStatusResponse,
  type KgReason,
  type UploadCapabilities,
} from '@/features/documents/types';
import { DeferredSharePointItem } from '@/features/sharepoint/types';
import { DeferredProcoreItem } from '@/features/procore/types';
import {
  DOCUMENT_EXTENSION_GROUPS,
  DOCUMENT_TYPE,
  DocumentFailureReason,
  type DocumentType,
  type DocumentTypeName,
} from '@/features/documents/constants';
import { downloadBlob } from '@/ui/utils';
import { t } from '@/features/i18n/utils';

export const uniqueReferences = <
  T extends { documentId?: string; document?: Document; title?: string },
>(
  references: T[],
  keyOf?: (reference: T, defaultKey: string) => string,
): T[] => {
  const seen = new Set<string>();
  const unique: T[] = [];
  for (const reference of references) {
    const defaultKey = reference.documentId ?? reference.document?.id ?? reference.title ?? '';
    const key = keyOf ? keyOf(reference, defaultKey) : defaultKey;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(reference);
  }
  return unique;
};

const UPPERCASE_CATEGORIES = new Set(['RFP']);

export const formatCategory = (value?: string | null) => {
  if (!value) return '-';
  if (value === 'MINUTES_OF_MEETING') return t({ en: 'Meeting note', nb: 'Møtereferat' });
  if (value === 'CHANGE_ORDERS') return t({ en: 'Change record', nb: 'Endringspost' });
  if (value === 'EMAIL') return t({ en: 'Correspondence', nb: 'Korrespondanse' });
  if (value === 'INVOICE') return t({ en: 'Invoice', nb: 'Faktura' });
  if (value === 'EXPENSE_CLAIM') return t({ en: 'Expense', nb: 'Utlegg' });
  if (value === 'CALCULATIONS') return t({ en: 'Calculation', nb: 'Beregning' });
  if (value === 'OTHER') return t({ en: 'Other', nb: 'Annet' });
  if (UPPERCASE_CATEGORIES.has(value)) return value;
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/[_-]/g, ' ');
};

const DOCUMENT_FAILURE_MESSAGES: Record<DocumentFailureReason, { en: string; nb: string }> = {
  [DocumentFailureReason.COPY_PROTECTED_PDF]: {
    en: "This PDF is copy-protected (DRM) and can't be read. Upload an unprotected copy.",
    nb: 'Denne PDF-filen er kopibeskyttet (DRM) og kan ikke leses. Last opp en ubeskyttet kopi.',
  },
  [DocumentFailureReason.PASSWORD_PROTECTED_PDF]: {
    en: 'This PDF is password protected. Unlock it and upload again.',
    nb: 'Denne PDF-filen er passordbeskyttet. Lås den opp og last opp på nytt.',
  },
  [DocumentFailureReason.FILE_UNAVAILABLE]: {
    en: "The stored file couldn't be retrieved. Upload it again.",
    nb: 'Den lagrede filen kunne ikke hentes. Last den opp på nytt.',
  },
  [DocumentFailureReason.PARSER_FAILED]: {
    en: "This file couldn't be read. It may be damaged or protected. Save it as a standard PDF and upload it again.",
    nb: 'Denne filen kunne ikke leses. Den kan være skadet eller beskyttet. Lagre den som en vanlig PDF og last den opp på nytt.',
  },
  [DocumentFailureReason.NO_READABLE_CONTENT]: {
    en: 'No readable content was found in this file.',
    nb: 'Fant ikke noe lesbart innhold i denne filen.',
  },
  [DocumentFailureReason.STANDARD_EXTRACTION_FAILED]: {
    en: "The clauses of this standard couldn't be extracted. Upload it again, and contact support if it keeps failing.",
    nb: 'Punktene i denne standarden kunne ikke hentes ut. Last den opp på nytt, og kontakt support hvis det fortsatt feiler.',
  },
  [DocumentFailureReason.UPLOAD_CANCELLED]: {
    en: 'The upload was cancelled before this file was processed. Upload it again.',
    nb: 'Opplastingen ble avbrutt før filen ble behandlet. Last den opp på nytt.',
  },
  [DocumentFailureReason.PROCESSING_INTERRUPTED]: {
    en: 'Processing stopped before this file was finished. Upload it again.',
    nb: 'Behandlingen stoppet før filen var ferdig. Last den opp på nytt.',
  },
  [DocumentFailureReason.UNKNOWN]: {
    en: "This file couldn't be processed. Upload it again, and contact support if it keeps failing.",
    nb: 'Denne filen kunne ikke behandles. Last den opp på nytt, og kontakt support hvis det fortsatt feiler.',
  },
};

const isDocumentFailureReason = (value?: string | null): value is DocumentFailureReason =>
  Object.values<string | null | undefined>(DocumentFailureReason).includes(value);

export const getDocumentFailureMessage = (failureReason?: string | null) =>
  t(
    isDocumentFailureReason(failureReason)
      ? DOCUMENT_FAILURE_MESSAGES[failureReason]
      : DOCUMENT_FAILURE_MESSAGES[DocumentFailureReason.UNKNOWN],
  );

export const downloadDocumentsZip = async (documents: { name: string; sasUri: string }[]) => {
  const zip = new JSZip();
  await Promise.all(
    documents.map(async (doc) => {
      const response = await fetch(doc.sasUri);
      zip.file(doc.name, await response.blob());
    }),
  );
  return downloadBlob(await zip.generateAsync({ type: 'blob' }), 'documents.zip');
};

export const downloadFileFromUrl = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    downloadBlob(await response.blob(), filename);
  } catch {
    if (window.open(url, '_blank', 'noopener,noreferrer')) return;
    toastManager.add({
      type: 'error',
      title: t({ en: 'Download failed', nb: 'Nedlasting mislyktes' }),
      description: t({
        en: 'The download was blocked by your browser. Please check your popup blocker settings and try again.',
        nb: 'Nedlastingen ble blokkert av nettleseren din. Sjekk innstillingene for popup-blokkering og prøv igjen.',
      }),
    });
  }
};

export const isKgDocumentType = (documentType: DocumentTypeName) =>
  documentType === 'tenderPackage' || documentType === 'contract';

export const isActiveKgReason = (reason: KgReason | null | undefined) =>
  reason === 'building' || reason === 'indexing';

export const canGenerateAgainstKg = (reason: KgReason | undefined) =>
  reason === 'fresh' || reason === 'stale' || reason === 'failed';

export enum KgEvaluationStatus {
  Failed = -20,
  NotStarted = 0,
  InProgress = 10,
  Done = 20,
}

type IndexingData = Pick<
  GetProxyIndexedDocumentsStatusResponse,
  'indexedPercentage' | 'kgStatus' | 'kgProgress' | 'documents'
>;

export const getIndexingState = (
  data: IndexingData | undefined,
  { waitForKg = false }: { waitForKg?: boolean } = {},
) => {
  const docProgress = data?.indexedPercentage ?? 0;
  const failedCount = data?.documents?.filter((d) => d.indexingError).length ?? 0;
  const docsResolved =
    docProgress === 100 ||
    (!!data?.documents?.length && data.documents.every((d) => d.indexed || d.indexingError));

  const hasKg = waitForKg && data?.kgStatus != null;
  const kgFailed = hasKg && data?.kgStatus === KgEvaluationStatus.Failed;
  const kgResolved = kgFailed || data?.kgStatus === KgEvaluationStatus.Done;

  return {
    progress:
      hasKg && docsResolved && !kgResolved ? Math.min(data?.kgProgress ?? 0, 99) : docProgress,
    isIndexed: docsResolved && (!hasKg || kgResolved),
    docsResolved,
    failedCount,
    kgFailed,
  };
};

export type DocumentIndexingSnapshot = {
  total?: number | null;
  indexed?: number | null;
  failed?: number | null;
  kg?: {
    status?: number | null;
    progress?: number | null;
    reason?: string | null;
  };
};

export const documentIndexingLiveUpdate = (
  payload: DocumentIndexingSnapshot,
  cached: IndexingData | undefined,
  listedFailed?: number,
): 'nudge' | 'skip' | { indexedPercentage: number } => {
  const total = payload.total;
  if (total == null) return 'nudge';
  if (total === 0) return 'skip';
  const indexedPercentage = Math.round(((payload.indexed ?? 0) / total) * 10000) / 100;
  if (cached?.indexedPercentage === indexedPercentage && payload.failed != null) {
    const cachedFailed = cached.documents?.filter((d) => d.indexingError && !d.indexed).length ?? 0;
    if (cachedFailed !== payload.failed) return { indexedPercentage };
    if (listedFailed != null && listedFailed !== payload.failed) return { indexedPercentage };
    return 'skip';
  }
  return { indexedPercentage };
};

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const XML_DECL = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

const DOCX_EXT_RE = /\.(docx|dotx)$/i;
const CONTENT_PART_RE = /^word\/(document\d*|header\d*|footer\d*|footnotes|endnotes)\.xml$/i;
const COMMENT_PART_RE =
  /^word\/(comments|commentsExtended|commentsIds|commentsExtensible|threadedComments)\.xml$/i;

const DROP_TAGS = ['del', 'moveFrom'];
const UNWRAP_TAGS = ['ins', 'moveTo'];
const MARKER_TAGS = [
  'commentRangeStart',
  'commentRangeEnd',
  'commentReference',
  'moveFromRangeStart',
  'moveFromRangeEnd',
  'moveToRangeStart',
  'moveToRangeEnd',
  'rPrChange',
  'pPrChange',
  'sectPrChange',
  'tblPrChange',
  'trPrChange',
  'tcPrChange',
  'tblGridChange',
  'numberingChange',
];

const isDocx = (file: File) => DOCX_EXT_RE.test(file.name);

const removeAll = (doc: XMLDocument, local: string) => {
  let el = doc.getElementsByTagNameNS(W_NS, local)[0];
  while (el) {
    el.parentNode?.removeChild(el);
    el = doc.getElementsByTagNameNS(W_NS, local)[0];
  }
};

const unwrapAll = (doc: XMLDocument, local: string) => {
  let el = doc.getElementsByTagNameNS(W_NS, local)[0];
  while (el) {
    const parent = el.parentNode;
    if (!parent) break;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
    el = doc.getElementsByTagNameNS(W_NS, local)[0];
  }
};

const transformXml = (xml: string, mutate: (doc: XMLDocument) => void) => {
  const doc = new DOMParser().parseFromString(xml.replace(/^\uFEFF/, ''), 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) return xml;
  mutate(doc);
  const out = new XMLSerializer().serializeToString(doc);
  return out.startsWith('<?xml') ? out : `${XML_DECL}\r\n${out}`;
};

const sanitizeBody = (xml: string) =>
  transformXml(xml, (doc) => {
    DROP_TAGS.forEach((t) => removeAll(doc, t));
    UNWRAP_TAGS.forEach((t) => unwrapAll(doc, t));
    MARKER_TAGS.forEach((t) => removeAll(doc, t));
  });

const cleanContentTypes = (xml: string) =>
  transformXml(xml, (doc) => {
    Array.from(doc.getElementsByTagName('Override')).forEach((o) => {
      const part = (o.getAttribute('PartName') ?? '').replace(/^\//, '');
      if (COMMENT_PART_RE.test(part)) o.parentNode?.removeChild(o);
    });
  });

const cleanRels = (xml: string) =>
  transformXml(xml, (doc) => {
    Array.from(doc.getElementsByTagName('Relationship')).forEach((r) => {
      const target = (r.getAttribute('Target') ?? '').replace(/^\//, '');
      const full = target.startsWith('word/') ? target : `word/${target}`;
      if (COMMENT_PART_RE.test(full)) r.parentNode?.removeChild(r);
    });
  });

const cleanSettings = (xml: string) => transformXml(xml, (doc) => removeAll(doc, 'trackChanges'));

const stripDocx = async (file: File): Promise<File> => {
  const zip = await JSZip.loadAsync(file);

  const commentParts: string[] = [];
  zip.forEach((path) => {
    if (COMMENT_PART_RE.test(path)) commentParts.push(path);
  });
  commentParts.forEach((p) => zip.remove(p));

  const tasks: Promise<void>[] = [];
  zip.forEach((path, entry) => {
    if (entry.dir) return;
    let clean: ((xml: string) => string) | undefined;
    if (CONTENT_PART_RE.test(path)) clean = sanitizeBody;
    else if (/^\[Content_Types\]\.xml$/i.test(path)) clean = cleanContentTypes;
    else if (/^word\/_rels\/.*\.rels$/i.test(path)) clean = cleanRels;
    else if (/^word\/settings\.xml$/i.test(path)) clean = cleanSettings;
    if (!clean) return;
    const apply = clean;
    tasks.push(
      entry.async('string').then((xml) => {
        zip.file(path, apply(xml));
      }),
    );
  });
  await Promise.all(tasks);

  const type = file.type || DOCX_MIME;
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', mimeType: type });
  return new File([blob], file.name, { type, lastModified: file.lastModified });
};

export const prepareDocxFilesForUpload = async (files: File[]) => {
  const prepared: File[] = [];
  for (const file of files) {
    prepared.push(isDocx(file) && file.size <= 32 * 1024 ** 2 ? await stripDocx(file) : file);
  }
  return prepared;
};

const HTML_TAGS = [
  'p',
  'br',
  'b',
  'i',
  'u',
  'strong',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'span',
  'div',
  'blockquote',
  'pre',
  'code',
  'sup',
  'sub',
];

export const sanitizeSnippetHtml = (html: string) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: HTML_TAGS,
    ALLOWED_ATTR: ['colspan', 'rowspan'],
    ALLOW_DATA_ATTR: false,
  });

export const sanitizeDocumentHtml = (html: string) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...HTML_TAGS, 'a', 'img'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'colspan', 'rowspan', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });

export const computeSha256 = async (file: File): Promise<string> => {
  if (file.size > 32 * 1024 ** 2) return '';
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

export const stagedFingerprint = (
  files: File[],
  sharepointItems: DeferredSharePointItem[],
  procoreItems: DeferredProcoreItem[],
) =>
  JSON.stringify([
    files.map((f) => [f.name, f.size, f.lastModified]),
    sharepointItems.map((item) => [item.name || item.itemId, item.size ?? 0]),
    procoreItems.map((item) => [item.name || item.itemId, item.size ?? 0]),
  ]);

export const documentExtensions = ({ formats }: { formats: { extensions: string[] }[] }) =>
  formats.flatMap((format) => format.extensions);

export const hasExtension = (name: string, extensions: string[]) =>
  extensions.some((extension) => name.toLowerCase().endsWith(extension.toLowerCase()));

export const documentExtensionGroups = (policy: UploadCapabilities) =>
  Map.groupBy(
    documentExtensions(policy.documents),
    (extension) =>
      (Object.keys(DOCUMENT_EXTENSION_GROUPS) as (keyof typeof DOCUMENT_EXTENSION_GROUPS)[]).find(
        (group) => DOCUMENT_EXTENSION_GROUPS[group].includes(extension),
      ) ?? ('other' as const),
  );

const DOCUMENT_TYPE_NAMES = Object.fromEntries(
  Object.entries(DOCUMENT_TYPE).map(([name, type]) => [type, name]),
) as Record<DocumentType, DocumentTypeName>;

export const documentTypeName = (type: DocumentType) => DOCUMENT_TYPE_NAMES[type];

export const docsRegexp = /\[(doc\d{1,3}(?:,\s*doc\d{1,3})*)]/g;

const exactDocRefRegexp = /^doc\d{1,3}$/;

export const getDocumentReferenceNumbersFromMatch = (matchedString: string) =>
  matchedString
    .slice(1, -1)
    .split(',')
    .map((docRef) => docRef.trim())
    .filter((docRef) => exactDocRefRegexp.test(docRef))
    .map((docRef) => +docRef.replace('doc', ''));

export const groupByFolder = <T>(items: T[], getPath: (item: T) => string, folder: string) => {
  const hasItemsIn = (path: string) =>
    items.some((item) => `${getPath(item)}/`.startsWith(`${path}/`));
  const nearestFolder = (path: string): string =>
    !path || hasItemsIn(path) ? path : nearestFolder(path.split('/').slice(0, -1).join('/'));
  const current = nearestFolder(folder);
  const prefix = current ? `${current}/` : '';
  return {
    current,
    prefix,
    items: items.filter((item) => getPath(item) === current),
    subfolders: Map.groupBy(
      items.filter((item) => getPath(item) !== current && getPath(item).startsWith(prefix)),
      (item) => getPath(item).slice(prefix.length).replace(/\/.*/, ''),
    ),
  };
};

export const getDocumentFolderPath = (document: { folderPath?: string | null }) =>
  document.folderPath ?? '';

export const getMoveItems = (rows: FilesPageRow[], target: string) =>
  rows.flatMap((row) => {
    if (row.type === 'document') return [{ documentId: row.id, folderPath: target }];
    const parent = row.id.split('/').slice(0, -1).join('/');
    return row.documents.map((doc) => ({
      documentId: doc.id,
      folderPath: [target, getDocumentFolderPath(doc).slice(parent.length).replace(/^\//, '')]
        .filter(Boolean)
        .join('/'),
    }));
  });
